import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SiteMobileHeader from '../components/SiteMobileHeader';
import homeHeroLogo from '../assets/Group12.svg';
import MailingListModal from '../components/MailingListModal';
import StickyNavigation from '../components/StickyNavigation';
import {
  founderCarousel1,
  founderCarousel2,
  ourStoryAboutShot,
  ourStoryGlassShot,
  ourStoryHeroShot,
} from '../cdn/ourStoryMedia';
import ourStoryWavy from '../assets/wavy.svg';
import { clearContactState, submitMailingListForm } from '../store/slices/contactSlice';
import { validateMailingListForm } from '../utils/mailingListValidation';
import { showToast } from '../utils/toast';
import useReplayOnScrollReveal from '../hooks/useReplayOnScrollReveal';
import useStaggeredRiseOnView, { staggerLineClass } from '../hooks/useStaggeredRiseOnView';
import './LandingPage.css';
import './OurStoryPage.css';
import '../styles/SitePageShell.css';
import HomeContactFooter from '../components/HomeContactFooter';

const OUR_STORY_HERO_TITLE_LINES = ['Invest in', 'Thoughtful Living'];

const OUR_STORY_HERO_LEDE_LINES = [
  'Neoterra Developments is a design-driven, future-focused',
  'real estate brand built on the belief that luxury should',
  'endure, inspire, and be created with conscience.',
];

const OUR_STORY_HERO_LEDE_LINES_MOBILE = [
  'Neoterra Developments is a design-driven,',
  'future-focused real estate brand built',
  'on the belief that luxury should endure,',
  'inspire, and be created with conscience.',
];

const OUR_STORY_ABOUT_PARAS = [
  {
    text: 'Backed by a legacy of excellence and a clear vision for the future, Neoterra is committed to creating distinctive places that enrich lifestyles, inspire meaningful experiences, and stand the test of time.',
    mobileLines: [
      'Backed by a legacy of excellence and a clear',
      'vision for the future, Neoterra is committed to',
      'creating distinctive places that enrich lifestyles,',
      'inspire meaningful experiences, and stand the',
      'test of time.',
    ],
  },
  {
    text: 'Neoterra Developments stands at the intersection of craftsmanship, innovation, and sustainability, thoughtfully redefining modern luxury through purposeful design, enduring quality, and spaces created for the way people live today and tomorrow.',
    mobileLines: [
      'Neoterra Developments stands at the intersection',
      'of craftsmanship, innovation, and sustainability,',
      'thoughtfully redefining modern luxury through',
      'purposeful design, enduring quality, and spaces',
      'created for the way people live today and',
      'tomorrow.',
    ],
  },
];

/*
 * Desktop (≥1024px, Tailwind `lg`): prefer `lg:` classes here or `@media (min-width: 1024px)` in
 * OurStoryPage.css so layouts below that width stay on the mobile-first base styles.
 */
const MAIL_MODAL_CLOSE_MS = 620;

const OUR_STORY_MISSION_PARAS = [
  'We shape experiences that define modern luxury, conceived with mastery, guided by creativity, and refined through excellence.',
  'Every Neoterra development embodies thoughtful design, creating environments that enrich life and endure through generations.',
];

const OUR_STORY_VISION_PARAS = [
  'Through design and conscience, we create landmarks that endure as expressions of beauty, precision, and craft.',
  'Neoterra builds spaces that elevate the way people live and leave a legacy of excellence that stands the test of time.',
];

const OUR_STORY_FUTURE_CARDS = [
  {
    id: 'vision',
    title: 'Vision',
    body: `${OUR_STORY_VISION_PARAS[0]} ${OUR_STORY_VISION_PARAS[1]}`,
  },
  {
    id: 'mission',
    title: 'Mission',
    body: `${OUR_STORY_MISSION_PARAS[0]} ${OUR_STORY_MISSION_PARAS[1]}`,
  },
  {
    id: 'values',
    title: 'Core Values',
    bodyLines: [
      'Thoughtful design, enduring quality, and',
      'responsible innovation guide Neoterra in',
      'creating refined spaces that elevate everyday',
      'living.',
    ],
  },
];

/** Values shown in the glass card */
const OUR_STORY_GLASS_AUTOPLAY_MS = 5000;
const OUR_STORY_ACCORDION_ITEMS = [
  {
    id: 'timeless',
    title: 'Timeless design',
    body: 'Creating architectural landmarks that leave a lasting legacy.',
  },
  {
    id: 'responsibility',
    title: 'Responsibility & conscience',
    body: 'Mindful development that respects communities and resources.',
  },
  {
    id: 'human',
    title: 'Human-centred thinking',
    body: 'Spaces shaped around how people live, connect, and feel.',
  },
];

const OUR_STORY_FOUNDERS_MESSAGES = [
  {
    id: 'naresh',
    image: founderCarousel1,
    name: 'Mr. Naresh Perwani',
    title: 'Chairman & Founder',
    imageSide: 'left',
    badgeAlign: 'right',
    paragraphs: [
      "Inspired by Dubai's visionary leadership, Mr. Naresh sees the city as a global benchmark for ambition, innovation, and business excellence. Its cosmopolitan environment, progressive outlook, and strong entrepreneurial ecosystem continue to provide businesses with the opportunities and resources needed to grow and succeed.",
      'This spirit has been a driving force behind his journey, with Dubai serving as a strong foundation for growth, expansion, and the creation of long-term value.',
    ],
  },
  {
    id: 'omesh',
    image: founderCarousel2,
    name: 'Mr. Omesh Gurnani',
    title: 'CEO & Founder',
    imageSide: 'right',
    badgeAlign: 'left',
    paragraphs: [
      "Inspired by Dubai's forward-looking vision and the Dubai 2040 Urban Master Plan, the CEO & Founder believes in creating developments that support the city's evolution into one of the world's most liveable and future-ready destinations.",
      'His vision is centered on delivering innovative, sustainable, and community-focused developments that combine quality, connectivity, thoughtful planning, and responsible urban living.',
    ],
  },
];

const splitFounderBadgeName = (fullName) => {
  const parts = fullName.trim().split(/\s+/);
  if (parts.length <= 1) {
    return { firstLine: fullName, lastLine: '' };
  }
  return {
    firstLine: parts.slice(0, -1).join(' '),
    lastLine: parts[parts.length - 1],
  };
};

const OurStoryPage = () => {
  const dispatch = useDispatch();
  const contactSubmitting = useSelector((state) => state.contact.loading);

  useReplayOnScrollReveal('.our-story-desktop-reveal', 'is-visible', 0.12, '80px 0px -10% 0px', false);
  useReplayOnScrollReveal(
    '.our-story-leaders-desktop__copy',
    'is-copy-visible',
    0.2,
    '0px 0px -12% 0px',
    false
  );

  const heroRevealRef = useRef(null);
  const aboutRevealRef = useRef(null);

  useStaggeredRiseOnView(heroRevealRef, {
    individual: true,
    durationMs: 1800,
    distancePx: 28,
    threshold: 0.01,
    rootMargin: '0px',
    replay: true,
    reverseOnLeave: true,
    desktopOnly: false,
  });

  useStaggeredRiseOnView(aboutRevealRef, {
    individual: true,
    durationMs: 1800,
    distancePx: 28,
    threshold: 0.14,
    rootMargin: '0px 0px -5% 0px',
    replay: true,
    reverseOnLeave: true,
    desktopOnly: false,
  });

  const [mailOpen, setMailOpen] = useState(false);
  const [mailClosing, setMailClosing] = useState(false);
  const [mailFullName, setMailFullName] = useState('');
  const [mailEmail, setMailEmail] = useState('');
  const [mailPhone, setMailPhone] = useState('');
  const [mailPromotionalConsent, setMailPromotionalConsent] = useState(false);

  const [glassValueIndex, setGlassValueIndex] = useState(0);
  const glassCardRef = useRef(null);

  const glassActiveValue = OUR_STORY_ACCORDION_ITEMS[glassValueIndex];

  const goGlassValuePrev = () => {
    setGlassValueIndex((index) =>
      index === 0 ? OUR_STORY_ACCORDION_ITEMS.length - 1 : index - 1
    );
  };

  const goGlassValueNext = () => {
    setGlassValueIndex((index) =>
      index === OUR_STORY_ACCORDION_ITEMS.length - 1 ? 0 : index + 1
    );
  };

  useEffect(() => {
    const card = glassCardRef.current;
    let timerId = 0;

    const stopAutoplay = () => {
      if (timerId) {
        window.clearInterval(timerId);
        timerId = 0;
      }
    };

    const startAutoplay = () => {
      stopAutoplay();
      timerId = window.setInterval(() => {
        setGlassValueIndex((index) =>
          index === OUR_STORY_ACCORDION_ITEMS.length - 1 ? 0 : index + 1
        );
      }, OUR_STORY_GLASS_AUTOPLAY_MS);
    };

    startAutoplay();
    if (card) {
      card.addEventListener('mouseenter', stopAutoplay);
      card.addEventListener('mouseleave', startAutoplay);
      card.addEventListener('focusin', stopAutoplay);
      card.addEventListener('focusout', startAutoplay);
    }

    return () => {
      stopAutoplay();
      if (card) {
        card.removeEventListener('mouseenter', stopAutoplay);
        card.removeEventListener('mouseleave', startAutoplay);
        card.removeEventListener('focusin', stopAutoplay);
        card.removeEventListener('focusout', startAutoplay);
      }
    };
  }, []);

  const openMailModal = () => {
    setMailClosing(false);
    setMailOpen(true);
  };

  const closeMailModal = () => {
    if (mailClosing || !mailOpen) return;
    setMailClosing(true);
    window.setTimeout(() => {
      setMailOpen(false);
      setMailClosing(false);
    }, MAIL_MODAL_CLOSE_MS);
  };

  const handleMailSubmit = async (e) => {
    e.preventDefault();
    const validation = validateMailingListForm({
      fullName: mailFullName,
      email: mailEmail,
      phone: mailPhone,
    });

    if (!validation.isValid) {
      showToast(validation.error, 'error');
      return;
    }

    const { name, email: emailValue, contactNumber } = validation.values;

    try {
      await dispatch(
        submitMailingListForm({
          name,
          email: emailValue,
          contactNumber,
          promotionalConsent: mailPromotionalConsent,
          sourcePage: 'our-story',
        })
      ).unwrap();
      showToast('Thanks! We will keep you informed.', 'success');
      setMailFullName('');
      setMailEmail('');
      setMailPhone('');
      setMailPromotionalConsent(false);
      closeMailModal();
    } catch (err) {
      showToast(typeof err === 'string' ? err : 'Something went wrong. Please try again.', 'error');
    } finally {
      dispatch(clearContactState());
    }
  };

  return (
    <div className="landing-page our-story-page site-page-shell">
      <SiteMobileHeader
        variant="homeHero"
        logoSrc={homeHeroLogo}
        barClassName="home-top-bar our-story-home-top-bar"
        onEnquireClick={openMailModal}
      />

      <section className="our-story-hero-screen" aria-label="Our story">
        <img
          src={ourStoryHeroShot}
          alt=""
          className="our-story-hero-img our-story-hero-img--desktop"
          fetchPriority="high"
          decoding="async"
        />
        <div
          ref={heroRevealRef}
          className="our-story-hero-copy our-story-hero-copy--desktop flex"
        >
          <h1 className="our-story-hero-title">
            {OUR_STORY_HERO_TITLE_LINES.map((line, index) => (
              <span
                key={line}
                className={`our-story-hero-title-line ${staggerLineClass}`}
                data-content-line
                data-reveal-delay={String(index * 150)}
              >
                {line}
              </span>
            ))}
          </h1>
          <p className="our-story-hero-lede">
            {OUR_STORY_HERO_LEDE_LINES_MOBILE.map((line, index) => (
              <span
                key={`lede-mobile-${line}`}
                className={`our-story-hero-lede-line our-story-hero-lede-line--mobile ${staggerLineClass}`}
                data-content-line
                data-reveal-delay={String((OUR_STORY_HERO_TITLE_LINES.length + index) * 80)}
              >
                {line}
              </span>
            ))}
            {OUR_STORY_HERO_LEDE_LINES.map((line, index) => (
              <span
                key={`lede-desktop-${line}`}
                className={`our-story-hero-lede-line our-story-hero-lede-line--desktop ${staggerLineClass}`}
                data-content-line
                data-reveal-delay={String((OUR_STORY_HERO_TITLE_LINES.length + index) * 150)}
              >
                {line}
              </span>
            ))}
          </p>
        </div>
        <StickyNavigation
          wrapperClassName="home-navigation-sticky our-story-navigation-sticky"
          onInquireClick={openMailModal}
        />
      </section>

      <section id="our-story-after-hero" className="our-story-narrative" aria-label="Our story" tabIndex={-1}>
        <section
          className="our-story-about-desktop block"
          aria-labelledby="our-story-about-heading"
        >
          <div className="our-story-about-desktop__inner">
            <figure className="our-story-about-desktop__figure">
              <img
                src={ourStoryAboutShot}
                alt="Architect sketching a Neoterra residence design."
                className="our-story-about-desktop__photo"
                width={524}
                height={648}
                loading="lazy"
                decoding="async"
              />
            </figure>
            <div ref={aboutRevealRef} className="our-story-about-desktop__copy">
              <p className="our-story-about-desktop__kicker">
                <span className="our-story-about-desktop__kicker-line" aria-hidden="true" />
                <span
                  className={staggerLineClass}
                  data-content-line
                  data-reveal-delay="0"
                >
                  About Neoterra
                </span>
              </p>
              <h2 id="our-story-about-heading" className="our-story-about-desktop__heading">
                {['Where Thoughtful Design', 'Shapes Timeless Living'].map((line, index) => (
                  <span
                    key={line}
                    className={`our-story-about-desktop__heading-line ${staggerLineClass}`}
                    data-content-line
                    data-reveal-delay={String((index + 1) * 150)}
                  >
                    {line}
                  </span>
                ))}
              </h2>
              {OUR_STORY_ABOUT_PARAS.map((para, index) => (
                <p
                  key={para.mobileLines[0]}
                  className={`our-story-about-desktop__para ${staggerLineClass}`}
                  data-content-line
                  data-reveal-delay={String((index + 4) * 150)}
                >
                  <span className="our-story-about-desktop__para-mobile lg:hidden">
                    {para.mobileLines.map((line) => (
                      <span key={line} className="our-story-about-desktop__para-line">
                        {line}
                      </span>
                    ))}
                  </span>
                  <span className="hidden lg:inline">{para.text}</span>
                </p>
              ))}
            </div>
          </div>
        </section>

        <section
          className="our-story-glass-desktop block"
          aria-labelledby="our-story-glass-heading"
        >
          <div className="our-story-glass-desktop__stage">
            <img
              src={ourStoryGlassShot}
              alt=""
              className="our-story-glass-desktop__bg"
              loading="lazy"
              decoding="async"
            />
            <div ref={glassCardRef} className="our-story-glass-desktop__card our-story-desktop-reveal">
              <div className="our-story-glass-desktop__card-main">
                <h2 id="our-story-glass-heading" className="our-story-glass-desktop__heading">
                  <span className="our-story-glass-desktop__heading-line">Neoterra</span>
                  <span className="our-story-glass-desktop__heading-line">Values</span>
                </h2>
                <div
                  key={glassActiveValue.id}
                  className="our-story-glass-desktop__slide"
                  aria-live="polite"
                >
                  <h3 className="our-story-glass-desktop__item-title">{glassActiveValue.title}</h3>
                  <p className="our-story-glass-desktop__item-text">
                    {glassActiveValue.id === 'human' ? (
                      <>
                        Spaces shaped around how people live,{' '}
                        <span className="whitespace-nowrap">connect, and feel.</span>
                      </>
                    ) : (
                      glassActiveValue.body
                    )}
                  </p>
                </div>
              </div>
              <div className="our-story-glass-desktop__nav">
                <button
                  type="button"
                  className="our-story-glass-desktop__nav-btn our-story-glass-desktop__nav-btn--prev"
                  onClick={goGlassValuePrev}
                  aria-label="Previous value"
                >
                  <svg
                    className="our-story-glass-desktop__nav-icon"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <rect
                      x="2.92"
                      y="11.25"
                      width="18.33"
                      height="1.5"
                      fill="currentColor"
                    />
                    <path
                      d="M10.32 5.18 L2.755 12 L10.32 18.817"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <button
                  type="button"
                  className="our-story-glass-desktop__nav-btn our-story-glass-desktop__nav-btn--next"
                  onClick={goGlassValueNext}
                  aria-label="Next value"
                >
                  <svg
                    className="our-story-glass-desktop__nav-icon"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <rect
                      x="2.75"
                      y="11.25"
                      width="18.33"
                      height="1.5"
                      fill="currentColor"
                    />
                    <path
                      d="M13.68 5.18 L21.245 12 L13.68 18.817"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
              <div
                className="our-story-glass-desktop__dots flex items-center justify-center lg:hidden"
                role="tablist"
                aria-label="Neoterra values"
              >
                {OUR_STORY_ACCORDION_ITEMS.map((item, index) => (
                  <button
                    key={item.id}
                    type="button"
                    role="tab"
                    aria-selected={index === glassValueIndex}
                    aria-label={item.title}
                    className={`our-story-glass-desktop__dot${index === glassValueIndex ? ' is-active' : ''}`}
                    onClick={() => setGlassValueIndex(index)}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="our-story-leaders-desktop block" aria-label="Messages from our founders">
          <div className="our-story-leaders-desktop__list">
            {OUR_STORY_FOUNDERS_MESSAGES.map((founder, index) => {
              const badgeName = splitFounderBadgeName(founder.name);
              return (
                <article
                  key={`leader-${founder.id}`}
                  className={`our-story-leaders-desktop__row our-story-desktop-reveal${founder.imageSide === 'right' ? ' our-story-leaders-desktop__row--reverse' : ''
                    }`}
                  style={{ '--leader-reveal-delay': `${index * 140}ms` }}
                >
                  <figure className="our-story-leaders-desktop__media">
                    <img
                      src={founder.image}
                      alt={founder.name}
                      className="our-story-leaders-desktop__photo"
                      width={514}
                      height={638}
                      loading="lazy"
                      decoding="async"
                    />
                    <div
                      className={`our-story-leaders-desktop__name-badge-blur our-story-leaders-desktop__name-badge-blur--${founder.badgeAlign}`}
                      aria-hidden="true"
                    >
                    </div>
                    <figcaption
                      className={`our-story-leaders-desktop__name-badge-wrap our-story-leaders-desktop__name-badge-wrap--${founder.badgeAlign}`}
                    >
                      <div className="our-story-leaders-desktop__name-badge">
                        <span className="our-story-leaders-desktop__name-badge-name">{badgeName.firstLine}</span>
                        {badgeName.lastLine ? (
                          <span className="our-story-leaders-desktop__name-badge-surname">{badgeName.lastLine}</span>
                        ) : null}
                      </div>
                    </figcaption>
                  </figure>
                  <div className="our-story-leaders-desktop__copy">
                    <p className="our-story-leaders-desktop__kicker">
                      <span className="our-story-leaders-desktop__kicker-line" aria-hidden="true" />
                      A Message From
                    </p>
                    <h2 className="our-story-leaders-desktop__title">{founder.title}</h2>
                    {founder.paragraphs.map((para) => (
                      <p key={para.slice(0, 28)} className="our-story-leaders-desktop__para">
                        {para}
                      </p>
                    ))}
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        <section
          className="our-story-future-desktop block"
          aria-labelledby="our-story-future-heading"
        >
          <div className="our-story-future-desktop__inner">
            <header className="our-story-future-desktop__header our-story-desktop-reveal">
              <p className="our-story-future-desktop__kicker">
                <span className="our-story-future-desktop__kicker-line" aria-hidden="true" />
                Our Principles
              </p>
              <h2 id="our-story-future-heading" className="our-story-future-desktop__heading">
                <span className="our-story-future-desktop__heading-mobile lg:hidden">
                  <span className="our-story-future-desktop__heading-line">Building a Thoughtful</span>
                  <span className="our-story-future-desktop__heading-line">Future</span>
                </span>
                <span className="hidden lg:inline">Building a Thoughtful Future</span>
              </h2>
              <p className="our-story-future-desktop__lede">
                <span className="our-story-future-desktop__lede-mobile lg:hidden">
                  <span className="our-story-future-desktop__lede-line">The vision, mission, and values that</span>
                  <span className="our-story-future-desktop__lede-line">guide every community we create.</span>
                </span>
                <span className="hidden lg:inline">
                  The vision, mission, and values that guide every community we create.
                </span>
              </p>
            </header>
            <div className="our-story-future-desktop__grid">
              {OUR_STORY_FUTURE_CARDS.map((card, cardIndex) => (
                <div
                  key={card.id}
                  className="our-story-future-desktop__grid-item our-story-desktop-reveal"
                  style={{ transitionDelay: `${120 + cardIndex * 100}ms` }}
                >
                  <article className="our-story-future-desktop__card">
                    <img
                      src={ourStoryWavy}
                      alt=""
                      aria-hidden="true"
                      className={`our-story-future-desktop__card-wave${cardIndex === 1
                        ? ' our-story-future-desktop__card-wave--top'
                        : ' our-story-future-desktop__card-wave--bottom'
                        }${cardIndex !== 3 ? ' our-story-future-desktop__card-wave--gradient' : ''}`}
                      decoding="async"
                    />
                    <h3 className="our-story-future-desktop__card-title">{card.title}</h3>
                    <p className="our-story-future-desktop__card-body">
                      {card.bodyLines ? (
                        card.bodyLines.map((line) => (
                          <span key={line} className="our-story-future-desktop__card-body-line">
                            {line}
                          </span>
                        ))
                      ) : (
                        card.body
                      )}
                    </p>
                  </article>
                </div>
              ))}
            </div>
          </div>
        </section>

      </section>

      <div className="relative z-[20] bg-[#FAFAFA99] lg:hidden">
        <HomeContactFooter sectionId="our-story-contact" />
      </div>

      <div className="site-page-footer-wrap relative z-[30] isolate hidden bg-[#162D24] lg:block">
        <HomeContactFooter variant="home" sectionId="our-story-contact" />
      </div>

      <MailingListModal
        open={mailOpen}
        closing={mailClosing}
        onClose={closeMailModal}
        onSubmit={handleMailSubmit}
        submitting={contactSubmitting}
        fullName={mailFullName}
        setFullName={setMailFullName}
        email={mailEmail}
        setEmail={setMailEmail}
        phone={mailPhone}
        setPhone={setMailPhone}
        promotionalConsent={mailPromotionalConsent}
        setPromotionalConsent={setMailPromotionalConsent}
        idPrefix="our-story-mail"
      />
    </div>
  );
};

export default OurStoryPage;
